import { useEffect, useMemo, useRef, useState } from "react";
import { CAP } from "../data/mockData";
import { isApiSuccess } from "../utils/apiResponse";
import { fetchDocumentList, buildDocumentCategories, financierDisplayName } from "../api/configApi";
import { getDocs, uploadDoc as uploadDocApi, removeDocs, saveDocs } from "../api/leadApi";

// Document upload/tagging for step 3 (drag-and-drop onto a requirement,
// picking from device, the bank/gov-id tagging bottom sheet) plus the
// preview and untag/remove confirmation dialogs it shares with the rest of
// the app. `active` gates the one-time document_list/get_docs fetch — true
// for step 3 of the create flow, and also for the post-application route
// (Send to Bank/NOA/Contract also need the same docs, keyed by the same lead).
export default function useDocuments({ loanId, active, financierList, selectedBanks, govIdTypes, flash, touchSaved }) {
  const fileRef = useRef(null);
  const seqRef = useRef(0);
  const docStepLoadedFor = useRef(null);

  const [docs, setDocs] = useState([]);
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [autoOpen, setAutoOpen] = useState(true);
  const [openCat, setOpenCat] = useState(null);
  const [showReqErrors, setShowReqErrors] = useState(false);
  const [target, setTarget] = useState(null);
  const [pending, setPending] = useState(null);
  const [over, setOver] = useState(null);
  const [drag, setDrag] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!active || !loanId || docStepLoadedFor.current === loanId) return;
    docStepLoadedFor.current = loanId;
    setCategoriesLoading(true);

    // document_list must resolve first — get_docs' rows only carry a raw
    // doc_id, and mapping that back to a "category-index" reqId (the id the
    // rest of this hook keys everything by) needs the categories built from it.
    fetchDocumentList(loanId)
      .then(({ data }) => {
        if (!isApiSuccess(data)) {
          console.error("document list fetch failed", data?.message);
          return [];
        }
        const cats = buildDocumentCategories(data.data?.document_list);
        setDocumentRequirements(cats);
        if (cats.length) setOpenCat((c) => (c == null ? cats[0].key : c));
        return cats;
      })
      .catch((err) => {
        console.error("document list fetch failed", err.response?.status, err.response?.data || err.message);
        return [];
      })
      .finally(() => setCategoriesLoading(false))
      .then((cats) => {
        const reqIdByDocId = new Map();
        (cats || []).forEach((c) => {
          (c.docIds || []).forEach((docId, i) => {
            if (docId != null) reqIdByDocId.set(docId, c.key + "-" + i);
          });
        });

        // Resolve the "which bank"/"which gov ID type" labels get_docs implies
        // via financier_id / id_type_id, so a reloaded doc shows its tag (e.g.
        // "Attached: BDO") instead of looking untagged despite already sitting
        // in the right requirement slot.
        const bankNameForFinancierId = (financierId) => {
          if (!financierId) return undefined;
          const f = (financierList ? financierList() : []).find((x) => Number(x.id) === Number(financierId));
          return f ? financierDisplayName(f) : undefined;
        };
        const govLabelForIdType = (idTypeId) => {
          if (idTypeId == null) return undefined;
          const o = (govIdTypes || []).find((g) => Number(g.value) === Number(idTypeId));
          return o?.label;
        };

        return getDocs(loanId).then(({ data }) => {
          if (!isApiSuccess(data) || !Array.isArray(data.data)) return;
          const mapped = data.data.map((rec) => ({
            id: "d" + rec.id,
            name: rec.name || "Document",
            size: "",
            from: rec.upload_type || "",
            when: rec.time || "",
            kind: /\.(jpe?g|png)$/i.test(rec.name || "") ? "IMG" : "PDF",
            prev: "placeholder-document-doc.png",
            // signed_url comes back null whenever the image-signing service
            // call fails/isn't configured (confirmed live — every doc's
            // signed_url is null in this environment); doc_path is already a
            // full public URL to the same bucket the financier logos load
            // from directly, so it's a safe fallback rather than showing nothing.
            prevUrl: rec.signed_url || rec.doc_path || null,
            leadDocId: rec.id,
            // Re-tag/untag re-saves the same file under another doc_id, so
            // keep its URL and current master ids.
            docPath: rec.doc_path || null,
            docMasterId: rec.doc_id,
            parentId: rec.parent_id,
            // A manually-uploaded doc already tied to a known requirement shows
            // up already tagged; an auto-fetched one (or one whose doc_id no
            // longer matches a requirement) lands in the untagged inbox.
            req: rec.auto_fetched === "manual" ? reqIdByDocId.get(rec.doc_id) ?? null : null,
            ...(rec.financier_id ? { bankName: bankNameForFinancierId(rec.financier_id) } : {}),
            ...(rec.doc_type ? { govType: govLabelForIdType(rec.id_type_id) } : {}),
          }));
          setDocs(mapped);
        });
      })
      .catch((err) => {
        console.error("get_docs failed", err.response?.status, err.response?.data || err.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, loanId]);

  const docsByReq = useMemo(() => {
    const map = new Map();
    docs.forEach((d) => {
      if (!d.req) return;
      if (!map.has(d.req)) map.set(d.req, []);
      map.get(d.req).push(d);
    });
    return map;
  }, [docs]);
  const docsFor = (reqId) => docsByReq.get(reqId) || [];
  const categories = documentRequirements;
  const findReq = (reqId) => {
    const [k, i] = reqId.split("-");
    const cat = categories.find((c) => c.key === k);
    return { label: cat?.reqs[+i], docId: cat?.docIds ? cat.docIds[+i] : undefined };
  };
  const reqLabel = (reqId) => findReq(reqId).label;

  // reqIds ("category-index") of required docs that have no file yet, in
  // category order — used to block Next on step 3 and highlight the rows.
  const missingRequired = () => {
    const missing = [];
    categories.forEach((c) => {
      (c.required || []).forEach((isReq, i) => {
        const reqId = c.key + "-" + i;
        if (isReq && docsFor(reqId).length === 0) missing.push(reqId);
      });
    });
    return missing;
  };

  // Called on Next: if anything required is missing, open the first category
  // with a gap (and keep it open) instead of letting the step advance.
  const validateRequired = () => {
    const missing = missingRequired();
    if (!missing.length) {
      setShowReqErrors(false);
      return true;
    }
    setShowReqErrors(true);
    setOpenCat(missing[0].split("-")[0]);
    return false;
  };
  const docIdForReq = (reqId) => findReq(reqId).docId;

  // The "which bank"/"which government ID" tagging bottom sheets are keyed to
  // two fixed master doc_ids (HOLE_LOAN_DOC=2, ID_TYPE_DOC=3), not a fixed
  // requirement position — resolve them from whichever category the real
  // document_list actually put them in. Both are null until document_list
  // has loaded (categories is empty), which is fine — nothing can be uploaded
  // to any requirement yet either at that point.
  const findReqIdForDocId = (docId) => {
    for (const c of categories) {
      const i = (c.docIds || []).indexOf(docId);
      if (i !== -1) return c.key + "-" + i;
    }
    return null;
  };
  const bankReqId = findReqIdForDocId(2);
  const govReqId = findReqIdForDocId(3);

  // HL application forms: one per selected financier, so that slot's cap is
  // the number of financiers on this lead (falls back to CAP until the
  // financier list has loaded); every other requirement keeps the fixed CAP.
  const bankCap = (selectedBanks ? selectedBanks().length : 0) || CAP;
  const capFor = (reqId) => (reqId && reqId === bankReqId ? bankCap : CAP);
  const fullMsg = (reqId) =>
    reqId === bankReqId
      ? "Only " + bankCap + " HL application form" + (bankCap > 1 ? "s" : "") + " allowed — one per selected financier"
      : "Maximum " + CAP + " files for this requirement";
  const bankHasForm = (bankName, exceptDocId) =>
    docs.some((d) => d.req === bankReqId && d.bankName === bankName && d.id !== exceptDocId);

  const idTypeIdForGovLabel = (label) => {
    const o = (govIdTypes || []).find((g) => g.label === label);
    return o ? Number(o.value) : undefined;
  };

  const fromFiles = (fileList) =>
    Array.from(fileList || []).map((file) => {
      const img = /^image\//.test(file.type);
      return {
        name: file.name,
        size: Math.max(1, Math.round(file.size / 1024)) + " KB",
        from: "this computer",
        kind: img ? "IMG" : "PDF",
        prev: img ? URL.createObjectURL(file) : "placeholder-document-doc.png",
        prevUrl: img ? URL.createObjectURL(file) : null,
        _file: file,
      };
    });

  const attach = (reqId, items) => {
    const room = capFor(reqId) - docsFor(reqId).length;
    if (room <= 0) {
      flash(fullMsg(reqId));
      return;
    }
    // A financier gets exactly one HL application form.
    if (reqId === bankReqId) {
      const bankName = items[0]?.bankName;
      if (bankName && bankHasForm(bankName)) {
        flash("A form is already attached for " + bankName);
        return;
      }
      if (items.length > 1) flash("Only one form per financier — attached the first file");
      items = items.slice(0, 1);
    }
    const take = items.slice(0, room);
    // parent_id is the requirement's own category id (document_list's root
    // node id) — required by upload_doc alongside doc_id.
    const [catKey] = reqId.split("-");
    const parentId = catKey ? Number(catKey) : undefined;
    // primary/secondary/additional is assigned by how many gov-ID docs already
    // sit in this requirement, in upload order — mirrors the Primary/Secondary
    // labelling already shown in the UI (DocumentsStep's govUsed).
    let govOrdinal = reqId === govReqId ? docsFor(reqId).length : 0;
    const added = take.map((it) => {
      seqRef.current += 1;
      const { _file, ...clean } = it;
      return { id: "u" + seqRef.current, size: "256 KB", from: "device", when: "Just now", kind: "PDF", prev: "placeholder-document-doc.png", ...clean, req: reqId };
    });
    take.forEach((it, idx) => {
      if (!it._file) return;
      const formData = new FormData();
      formData.append("images", it._file);
      if (loanId) formData.append("lead_id", loanId);
      const docId = docIdForReq(reqId);
      if (docId != null) formData.append("doc_id", docId);
      if (parentId != null) formData.append("parent_id", parentId);
      formData.append("upload_type", "manual");
      if (reqId === bankReqId && it.financierId != null) {
        formData.append("financier_id", it.financierId);
      }
      if (reqId === govReqId) {
        const idTypeId = idTypeIdForGovLabel(it.govType);
        if (idTypeId != null) formData.append("id_type_id", idTypeId);
        formData.append("doc_type", govOrdinal === 0 ? "primary" : govOrdinal === 1 ? "secondary" : "additional");
        govOrdinal += 1;
      }
      // upload_doc doesn't return the saved row's id — keep its file_url so a
      // later remove can find the row in get_docs (doc_path is that same URL).
      const localId = added[idx].id;
      uploadDocApi(formData)
        .then(({ data }) => {
          const docPath = isApiSuccess(data) && Array.isArray(data.data) ? data.data[0]?.file_url : null;
          if (docPath) setDocs((st) => st.map((d) => (d.id === localId ? { ...d, docPath } : d)));
        })
        .catch((err) => {
          console.error("upload_doc failed", err.response?.status, err.response?.data || err.message);
        });
    });
    setDocs((st) => [...st, ...added]);
    setOpenCat(reqId.split("-")[0]);
    setPending(null);
    setTarget(null);
    setOver(null);
    touchSaved();
    flash(take.length + (take.length > 1 ? " files added" : " file added"));
  };

  const receive = (reqId, items) => {
    if (!items.length) return;
    if (docsFor(reqId).length >= capFor(reqId)) {
      flash(fullMsg(reqId));
      setOver(null);
      return;
    }
    if (reqId === bankReqId) {
      setSheet({ kind: "bank" });
      setPending(items);
      setTarget(reqId);
      setOver(null);
      return;
    }
    if (reqId === govReqId) {
      setSheet({ kind: "gov" });
      setPending(items);
      setTarget(reqId);
      setOver(null);
      return;
    }
    attach(reqId, items);
  };

  const pickFiles = (reqId) => {
    setTarget(reqId);
    if (fileRef.current) fileRef.current.click();
  };

  const tagTo = (docId, reqId) => {
    if (docsFor(reqId).length >= capFor(reqId)) {
      flash(fullMsg(reqId));
      return;
    }
    if (reqId === bankReqId) {
      setSheet({ kind: "bank", docId });
      return;
    }
    if (reqId === govReqId) {
      setSheet({ kind: "gov", docId });
      return;
    }
    setSheet(null);
    setDrag(null);
    setOver(null);
    setOpenCat(reqId.split("-")[0]);
    retag(docId, { req: reqId }, "Tagged to " + reqLabel(reqId));
  };

  const tagGov = (docId, label) => {
    setSheet(null);
    setDrag(null);
    setOver(null);
    setOpenCat(govReqId?.split("-")[0]);
    retag(docId, { req: govReqId, govType: label }, "Tagged as " + label);
  };
  const tagBank = (docId, bankName) => {
    if (bankHasForm(bankName, docId)) {
      flash("A form is already attached for " + bankName);
      return;
    }
    setSheet(null);
    setDrag(null);
    setOver(null);
    setOpenCat(bankReqId?.split("-")[0]);
    retag(docId, { req: bankReqId, bankName }, "Form attached for " + bankName);
  };

  const onFiles = (e) => {
    const items = fromFiles(e.target.files);
    const t = target;
    e.target.value = "";
    if (t) receive(t, items);
  };

  const previewDoc = docs.find((d) => d.id === preview);

  const closeConfirm = () => setConfirm(null);
  // save_docs "remove" needs the rem_lead_docs id: known for docs loaded from
  // get_docs, looked up by doc_path for ones uploaded this session.
  const resolveLeadDocId = async (doc) => {
    if (doc.leadDocId != null) return doc.leadDocId;
    if (!doc.docPath) return null;
    const { data } = await getDocs(loanId);
    if (!isApiSuccess(data) || !Array.isArray(data.data)) return null;
    // Several rows can share one file (e.g. auto-fetched + tagged copies);
    // the one this card stands for is the newest.
    const ids = data.data.filter((rec) => rec.doc_path === doc.docPath).map((rec) => rec.id);
    return ids.length ? Math.max(...ids) : null;
  };

  // Tag/untag is persisted in one save_docs call: a new row for the file under
  // the target requirement's doc_id/parent_id, plus a remove of the old row.
  // upload_type decides where get_docs puts it on reload: "general_docuemnts"
  // (sic — the value the backend reads as manual) comes back tagged,
  // "auto_fetched" lands in the untagged inbox. The card moves right away and
  // moves back if the call fails.
  const retag = async (docId, patch, okMsg) => {
    const doc = docs.find((d) => d.id === docId);
    if (!doc) return;
    const revert = () => setDocs((st) => st.map((d) => (d.id === docId ? doc : d)));
    setDocs((st) => st.map((d) => (d.id === docId ? { ...d, ...patch } : d)));
    const fail = (msg) => {
      revert();
      flash(msg);
    };
    try {
      const leadDocId = await resolveLeadDocId(doc);
      if (leadDocId == null || !doc.docPath) {
        fail("Could not update document — try again once the upload finishes");
        return;
      }
      const next = { ...doc, ...patch };
      let entry;
      if (next.req) {
        const [catKey] = next.req.split("-");
        entry = { doc_id: docIdForReq(next.req), parent_id: Number(catKey), doc_path: doc.docPath, upload_type: "general_docuemnts" };
        if (next.req === bankReqId) {
          const f = (financierList ? financierList() : []).find((x) => financierDisplayName(x) === next.bankName);
          if (f) entry.financier_id = Number(f.id);
        }
        if (next.req === govReqId) {
          const idTypeId = idTypeIdForGovLabel(next.govType);
          if (idTypeId != null) entry.id_type_id = idTypeId;
          const ordinal = docsFor(govReqId).filter((d) => d.id !== docId).length;
          entry.doc_type = ordinal === 0 ? "primary" : ordinal === 1 ? "secondary" : "additional";
        }
      } else {
        // Untag keeps the file's current doc_id/parent_id, just flagged
        // auto_fetched again.
        const [catKey] = (doc.req || "").split("-");
        const docMasterId = doc.req ? docIdForReq(doc.req) : doc.docMasterId;
        const parentId = doc.req ? Number(catKey) : doc.parentId;
        entry = { doc_id: docMasterId, parent_id: parentId, doc_path: doc.docPath, upload_type: "auto_fetched" };
      }
      const { data } = await saveDocs(Number(loanId), [entry, { id: leadDocId, action: "remove" }]);
      if (!isApiSuccess(data)) {
        fail(data?.message || "Could not update document");
        return;
      }
      // save_docs doesn't return the new row's id; the next remove/re-tag
      // looks it up again by doc_path.
      setDocs((st) =>
        st.map((d) => (d.id === docId ? { ...d, leadDocId: null, docMasterId: entry.doc_id, parentId: entry.parent_id } : d))
      );
      touchSaved();
      flash(okMsg);
    } catch (err) {
      console.error("save_docs re-tag failed", err.response?.status, err.response?.data || err.message);
      fail(err.response?.data?.message || "Could not update document");
    }
  };

  const removeDoc = async (docId) => {
    const doc = docs.find((d) => d.id === docId);
    if (!doc) return;
    setConfirm(null);
    try {
      const leadDocId = await resolveLeadDocId(doc);
      if (leadDocId == null) {
        flash("Could not remove document — try again once the upload finishes");
        return;
      }
      const { data } = await removeDocs(Number(loanId), [leadDocId]);
      if (!isApiSuccess(data)) {
        flash(data?.message || "Could not remove document");
        return;
      }
      setDocs((st) => st.filter((d) => d.id !== docId));
      touchSaved();
      flash("Document removed");
    } catch (err) {
      console.error("save_docs remove failed", err.response?.status, err.response?.data || err.message);
      flash(err.response?.data?.message || "Could not remove document");
    }
  };

  const confirmOk = () => {
    if (!confirm) return;
    if (confirm.kind === "untag") {
      setConfirm(null);
      setAutoOpen(true);
      retag(confirm.id, { req: null, govType: undefined, bankName: undefined }, "Moved back to Auto-Fetched Docs");
      return;
    }
    removeDoc(confirm.id);
  };

  return {
    fileRef,
    seqRef,
    docs,
    setDocs,
    docsFor,
    reqLabel,
    bankReqId,
    govReqId,
    capFor,
    categories,
    categoriesLoading,
    autoOpen,
    setAutoOpen,
    openCat,
    setOpenCat,
    showReqErrors,
    missingRequired,
    validateRequired,
    target,
    setTarget,
    pending,
    setPending,
    over,
    setOver,
    drag,
    setDrag,
    sheet,
    setSheet,
    confirm,
    setConfirm,
    preview,
    setPreview,
    previewDoc,
    closeConfirm,
    confirmOk,
    fromFiles,
    attach,
    receive,
    pickFiles,
    tagTo,
    tagGov,
    tagBank,
    onFiles,
  };
}
